import { fromJS } from "immutable";
import * as React from "react";
import * as ReactGA from "react-ga";
import { renderToStaticMarkup } from "react-dom/server";
import { Language, LocalizeContextProps, withLocalize } from "react-localize-redux";
import { BrowserRouter, Route, Switch } from "react-router-dom";
import enTranslations from "../fallback-translations/en.json";
import fiTranslations from "../fallback-translations/fi.json";
import svTranslations from "../fallback-translations/sv.json";
import parseUrlParams from "../helpers/urlparam";
import IOffer from "../models/Offer";
import IPrice from "../models/Price";
import ApiService from "../services/ApiService";
import OfferService from "../services/OfferService";
import "./App.scss";
import Calculator from "./Calculator";
import Finished from "./Finished";
import NotFound from "./NotFound";
import ScrollToTop from "./ScrollToTop";
import Summary from "./Summary";
import ContactRequest from "./ContactRequest";

interface IState {
  currentOfferIdx: number;
  emailSentAddress: string;
  emailSentStatus: boolean | undefined;
  language: Language;
  offers: IOffer[];
}
type IProps = {} & LocalizeContextProps;
class App extends React.Component<IProps, IState> {
  private offerService: OfferService = new OfferService();
  private api: ApiService = new ApiService();
  private router: React.RefObject<any> = React.createRef();

  // This is immutable to be used when making new offers.
  // Typed as `any` so `.toJS()` yields a plain offer object: immutable v4
  // gives `toJS()` a strict structural type that no longer matches IOffer.
  private defaultOffer: any = fromJS({
    description: "",
    expirationUTC: (() => {
      const dt = new Date();
      dt.setDate(dt.getDate() + 30);
      return dt;
    })(),
    isContactRequest: false,
    offerContact: {},
    offerCreator: {},
    selection: {
      businessOrganization: 1,
      companySize: 35, // CompanySize35: "under 0.1 Meur",
      extraServices: [
        { sku: "NV-pal", selected: false, quantity: 0, disabled: false },
        { sku: "NV-mkl", selected: false, quantity: 0 },
        { sku: "NV-kons", selected: false, quantity: 0 },
        { sku: "NV-rb", selected: false, quantity: 0 },
        { sku: "NV-cont", selected: false, quantity: 0 },
        { sku: "NV-verk-p", selected: false, quantity: 0 },
        { sku: "NV-tri", selected: false, quantity: 0 },
        { sku: "NV-net", selected: true },
        { sku: "NV-til", selected: false, quantity: 0, showQuantity: 0 },
        { sku: "NV-toi", selected: false, quantity: 0 },
      ],
      product: {
        sku: "",
      },
      purchases: [
        { quantity: 0, sku: "NV-vlv", disabled: false },
        { quantity: 0, sku: "NV-mak", disabled: false },
      ],
      sales: [
        { quantity: 0, sku: "NV-vll", disabled: false },
        { quantity: 0, sku: "NV-vii", disabled: false },
      ],
    },
  });

  private languages = [
    { name: "Suomi", code: "fi" },
    { name: "English", code: "en" },
    { name: "Svenska", code: "sv" },
  ];

  constructor(props: IProps) {
    super(props);

    this.props.initialize({
      languages: this.languages,
      options: { renderToStaticMarkup },
    });

    let activeLang: Language = { ...this.languages[0], active: true };
    const fromParams = parseUrlParams().language;
    const fromLocalStorage = localStorage.getItem("NetvisorSOPOfferLang");
    if (fromLocalStorage || fromParams) {
      const foundLang = this.languages.find((lang: any) => lang.code === fromParams || lang.code === fromLocalStorage);
      if (foundLang) {
        activeLang = { ...foundLang, active: true };
      }
    }

    this.props.setActiveLanguage(activeLang.code);

    this.props.addTranslationForLanguage(enTranslations, "en");
    this.props.addTranslationForLanguage(fiTranslations, "fi");
    this.props.addTranslationForLanguage(svTranslations, "sv");

    this.api.getTranslation(activeLang.code).subscribe(
      (langData: any) => {
        this.props.addTranslationForLanguage(langData, activeLang.code);
        this.languages.forEach((lang: any) => {
          if (lang.code !== activeLang.code) {
            this.api.getTranslation(lang.code).subscribe((otherLangData: any) => {
              this.props.addTranslationForLanguage(otherLangData, lang.code);
            });
          }
        });
      },
      () => {
        console.error("Cant get translations");
      }
    );

    let offers = [this.defaultOffer.toJS()];

    let currentOfferIdx = 0;

    this.defaultOfferInitialize(offers, currentOfferIdx);

    if (sessionStorage.getItem("NetvisorSOPOfferIdx")) {
      currentOfferIdx = +(sessionStorage.getItem("NetvisorSOPOfferIdx") || 0);
    }
    if (sessionStorage.getItem("NetvisorSOPOffers")) {
      offers = JSON.parse(sessionStorage.getItem("NetvisorSOPOffers") || "");
      offers.forEach((offer: IOffer) => {
        offer.expirationUTC = new Date(offer.expirationUTC || "");
      });
    }

    this.state = {
      currentOfferIdx,
      emailSentAddress: "",
      emailSentStatus: undefined,
      language: activeLang,
      offers,
    };

    this.handleDeleteOffer = this.handleDeleteOffer.bind(this);
    this.handleEditOffer = this.handleEditOffer.bind(this);
    this.handleEmailStatus = this.handleEmailStatus.bind(this);
    this.handleLangChanged = this.handleLangChanged.bind(this);
    this.handleNewOffer = this.handleNewOffer.bind(this);
    this.handleOffersUpdate = this.handleOffersUpdate.bind(this);
    this.handleOfferUpdate = this.handleOfferUpdate.bind(this);
    this.handleOfferUpdateAndExpand = this.handleOfferUpdateAndExpand.bind(this);
    this.handlePushOffer = this.handlePushOffer.bind(this);
    this.renderCalculator = this.renderCalculator.bind(this);
    this.renderContactRequest = this.renderContactRequest.bind(this);
    this.renderFinished = this.renderFinished.bind(this);
    this.renderNotFound = this.renderNotFound.bind(this);
    this.renderSummary = this.renderSummary.bind(this);
  }

  public render() {
    document.title = "Netvisor " + this.props.translate("SOP", undefined, { onMissingTranslation: () => "tarjouslaskuri" });
    return (
      <BrowserRouter ref={this.router}>
        <ScrollToTop>
          <Switch>
            <Route exact={true} path="/" render={this.renderCalculator} />
            <Route path="/summary" render={this.renderSummary} />
            <Route path="/contactRequest" render={this.renderContactRequest} />
            <Route path="/finished" render={this.renderFinished} />
            {process.env.REACT_APP_PRODUCTION !== "true" && <Route path="/embedTesting" render={this.renderEmbed} />}
            <Route render={this.renderNotFound} />
          </Switch>
        </ScrollToTop>
      </BrowserRouter>
    );
  }

  public componentDidMount() {
    this.initializeReactGA();
  }

  private initializeReactGA() {
    if (process.env.REACT_APP_PRODUCTION === "true") {
      ReactGA.initialize("UA-40904163-6");
      ReactGA.pageview("/");
    }
  }

  private renderEmbed(props: any) {
    return (
      <div className="d-flex flex-column flex-grow-1" style={{ height: "100vh", overflow: "hidden" }}>
        <header>Site to test the embedding</header>
        <embed className="flex-fill" src="http://netvisorhinta.azurewebsites.net" />
        {/* https://hinta.netvisor.fi */}
        <footer>Here's some footer</footer>
      </div>
    );
  }

  private renderCalculator(props: any) {
    return (
      <Calculator
        {...props}
        language={this.state.language}
        languageChanged={this.handleLangChanged}
        offer={this.state.offers[this.state.currentOfferIdx]}
        updateAndExpandOffer={this.handleOfferUpdateAndExpand}
        updateOffers={this.handleOffersUpdate}
      />
    );
  }
  private renderSummary(props: any) {
    return (
      <Summary
        {...props}
        language={this.state.language}
        languageChanged={this.handleLangChanged}
        offer={this.state.offers[this.state.currentOfferIdx]}
        offers={this.state.offers}
        updateEmailSentStatus={this.handleEmailStatus}
        updateOffer={this.handleOfferUpdate}
        pushOffer={this.handlePushOffer}
        editOffer={this.handleEditOffer}
        deleteOffer={this.handleDeleteOffer}
      />
    );
  }
  private renderContactRequest(props: any) {
    return (
      <ContactRequest
        {...props}
        language={this.state.language}
        languageChanged={this.handleLangChanged}
        offer={this.state.offers[this.state.currentOfferIdx]}
        offers={this.state.offers}
        updateEmailSentStatus={this.handleEmailStatus}
        updateOffer={this.handleOfferUpdate}
        pushOffer={this.handlePushOffer}
        editOffer={this.handleEditOffer}
        deleteOffer={this.handleDeleteOffer}
      />
    );
  }
  private renderFinished(props: any) {
    return (
      <Finished
        {...props}
        language={this.state.language}
        languageChanged={this.handleLangChanged}
        offers={this.state.offers}
        newOffer={this.handleNewOffer}
        emailSentStatus={this.state.emailSentStatus}
        emailSentAddress={this.state.emailSentAddress}
      />
    );
  }
  private renderNotFound(props: any) {
    return <NotFound {...props} language={this.state.language} languageChanged={this.handleLangChanged} />;
  }

  private handleOfferUpdate(offer: IOffer) {
    if (offer) {
      const offers = [...this.state.offers];
      offers[this.state.currentOfferIdx] = offer;
      this.setState({ offers });
      sessionStorage.setItem("NetvisorSOPOffers", JSON.stringify(offers));
    }
  }

  private handleOffersUpdate(offers: IOffer[], currentToken: string) {
    if (offers && offers.length) {
      let currentOfferIdx = offers.findIndex((o: IOffer) => o.token === currentToken);
      if (currentOfferIdx === -1) {
        currentOfferIdx = 0;
      }
      this.setState({ offers, currentOfferIdx });
      sessionStorage.setItem("NetvisorSOPOffers", JSON.stringify(offers));
      sessionStorage.setItem("NetvisorSOPOfferIdx", currentOfferIdx.toString());
    }
  }

  private defaultOfferInitialize(offers: any, currentOfferIdx: number) {
    if (process.env.REACT_APP_FE_MODE === "embed") {
      const currentOffer: IOffer = offers[currentOfferIdx];
      currentOffer.offerCreator = {
        businessID: "1967543-8",
        companyName: "Visma Solutions Oy",
        emailAddress: process.env.REACT_APP_PRODUCTION !== "true" ? "juha.kuokkanen@visma.com" : "myynti.netvisor@visma.com",
        firstName: "Netvisor",
        lastName: "Myynti",
        phoneNumber: "0107783700",
      };
    }
    // Some dummy test data
    if (window.location.hostname === "localhost") {
      const currentOffer: IOffer = offers[currentOfferIdx];
      currentOffer.offerCreator = {
        businessID: "666-7",
        companyName: "Test company Oy",
        emailAddress: "andrey.baklanov@visma.com",
        firstName: "Riku",
        lastName: "Kallio",
        phoneNumber: "050123456789",
      };
      currentOffer.offerContact = {
        businessID: "123-4",
        companyName: "Test company Oy",
        emailAddress: "matti@testiyritys.com",
        firstName: "Matti",
        lastName: "Meikäläinen",
        phoneNumber: "040987654321",
      };
    }
  }

  private handleNewOffer() {
    const offers = [this.defaultOffer.toJS()];
    this.defaultOfferInitialize(offers, 0);
    this.setState({ offers, currentOfferIdx: 0 });
    sessionStorage.setItem("NetvisorSOPOffers", JSON.stringify(offers));
    sessionStorage.setItem("NetvisorSOPOfferIdx", "0");
  }

  private handleOfferUpdateAndExpand(offer: IOffer, catalogData: any, priceData: IPrice) {
    offer = this.offerService.bundleOffer(offer, catalogData, priceData);

    const offers = [...this.state.offers];
    offers[this.state.currentOfferIdx] = offer;
    this.setState({ offers });
    sessionStorage.setItem("NetvisorSOPOffers", JSON.stringify(offers));
  }

  private handlePushOffer(offerParams: any) {
    const newOffer = Object.assign(this.defaultOffer.toJS(), offerParams);
    const offers = [...this.state.offers];
    offers.push(newOffer);
    const currentOfferIdx = offers.length - 1; // update current offer index to the last index
    this.setState({ offers, currentOfferIdx });
    sessionStorage.setItem("NetvisorSOPOffers", JSON.stringify(offers));
    sessionStorage.setItem("NetvisorSOPOfferIdx", currentOfferIdx.toString());
  }

  private handleEditOffer(offerToken: string) {
    const foundOfferIdx = this.state.offers.findIndex((offer: IOffer) => offer.token === offerToken);
    if (foundOfferIdx !== -1) {
      this.setState({ currentOfferIdx: foundOfferIdx });
      sessionStorage.setItem("NetvisorSOPOfferIdx", foundOfferIdx.toString());
      if (this.router.current && this.router.current.history && this.router.current.history.push) {
        this.router.current.history.push("/");
      }
    }
  }

  private handleDeleteOffer(offerToken: string) {
    const foundOfferIdx = this.state.offers.findIndex((offer: IOffer) => offer.token === offerToken);
    if (foundOfferIdx !== -1) {
      const offers = [...this.state.offers];
      offers.splice(foundOfferIdx, 1);
      let currentOfferIdx = this.state.currentOfferIdx;
      if (this.state.currentOfferIdx > 0 && this.state.currentOfferIdx >= foundOfferIdx) {
        currentOfferIdx--;
      }
      this.setState({ offers, currentOfferIdx });
      sessionStorage.setItem("NetvisorSOPOffers", JSON.stringify(offers));
      sessionStorage.setItem("NetvisorSOPOfferIdx", currentOfferIdx.toString());
    }
  }

  private handleEmailStatus(success: boolean, address: string) {
    this.setState({ emailSentStatus: success, emailSentAddress: address });
  }

  private handleLangChanged(language: Language) {
    this.props.setActiveLanguage(language.code);
    localStorage.setItem("NetvisorSOPOfferLang", language.code);
    this.setState({ language }, () => {
      // Tooltips texts must be fixed after language changed
      const tooltips = $('[data-toggle="tooltip"]') as JQuery<HTMLElement>;
      tooltips.attr("data-original-title", function () {
        return $(this).attr("title") || "";
      });
    });
  }
}

export default withLocalize(App);
